import { useContext, useMemo } from 'react';
import {
  IValidateFn,
  IFieldValidations,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import KonteringContext from '../../../../_Common/Components/KonteringManager/KonteringContext';
import { getKodstrangar } from '../Components/Kontering/Utils/KonteringUtils';
import { FakturaDelQuery_node_TEDLRFakturaDel } from '../Queries/_QueryTypes/FakturaDelQuery';
import { isNumberWithinMarginOfError } from '../Utils/FakturadelUtils';
import { FakturaattestWrapperContext } from '../../../Rutiner/FakturaDel/Contexts/FakturaattestWrapperContext';
import {
  TEDLRFakturaAttestAttestTypEnum,
  TEDLRFakturaDelAttestMinaStatusEnum,
  TEDLRLeverantorfakturaMOMSHANTERINGEnum,
} from '../../../../../../globalQueryTypes';
import {
  MOMS_MOMSSATS_DEFAULT_VALUE,
  MOMSSATS_TOLERANS_DEFAULT_VALUE,
} from '../Consts/FakturadelConsts';
import {
  validateSvenskDeletePreselectedMomssats,
  validateSvenskIngaendeMomsbeloppGreaterThanTolkatMomsbelopp,
  validateNoMomssatsWhenTolkatMomsbeloppIsZero,
  validateSvenskNoPreselectedMomssats,
  validateSvenskChangePreselectedMomssats,
} from '../Utils/FakturadelValidationUtils';
import { LR_MOMSHANTERING_LEVFAKT_MOMSATSTOLERANS } from '../Consts/FakturaDelSysoptionConsts';

export const useValidateSvenskMoms: () => IValidateFn<FakturaDelQuery_node_TEDLRFakturaDel> =
  () => {
    const konteringContext = useContext(KonteringContext);
    const fakturaattestContext = useContext(FakturaattestWrapperContext);

    const sysMomsTolerans = useMemo(() => {
      return fakturaattestContext.sysOptions?.find(
        sysopt => sysopt.OptionKey === LR_MOMSHANTERING_LEVFAKT_MOMSATSTOLERANS
      );
    }, [fakturaattestContext.sysOptions]);

    return ({ currentValues, data, touched }) => {
      const errors: IFieldValidations = [];
      const momshantering =
        currentValues?._Associations?.Faktura?.MOMSHANTERING;
      if (
        momshantering ===
          TEDLRLeverantorfakturaMOMSHANTERINGEnum.UTLANDSK__ENLIGT_KODDEL ||
        momshantering ===
          TEDLRLeverantorfakturaMOMSHANTERINGEnum.UTLANDSK__ENLIGT_LEVERANTOR
      ) {
        return errors;
      }

      const konteringsforslagArray =
        currentValues?._Associations?.Faktura?._Associations
          ?.Konteringsforslag_All;
      const kalkMomssats = currentValues?._Associations?.Faktura?.KalkMomssats;

      const attestTyp: TEDLRFakturaAttestAttestTypEnum =
        currentValues?.ATTEST_STATUS === 'BESLUTSATTESTERAS'
          ? currentValues?.AttestMinaStatus ===
            TEDLRFakturaDelAttestMinaStatusEnum.KONTROLLATTESTERAS
            ? TEDLRFakturaAttestAttestTypEnum.KONTROLLATTEST
            : TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST
          : TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST;

      const tolkatMomsbelopp =
        currentValues?._Associations?.Faktura?.TolkatMomsbelopp ??
        currentValues.FakturaBelopp2 * MOMS_MOMSSATS_DEFAULT_VALUE;

      const momsbeloppTolerans =
        currentValues?._Associations?.Faktura?.Momsbelopptolerans ?? 0;

      const kodplanId =
        data?._Associations?.Faktura?._Associations?.Registrering?._Associations
          ?.Bokfverifikationer?._Associations?.Kodplan?.id ?? '';
      const bokfDatum = new Date(
        data?._Associations?.Faktura?._Associations?.Registrering?._Associations?.Bokfverifikationer?.DATUM
      );

      const skipMomsValidation =
        currentValues?._Associations?.Faktura?._Associations?.Leverantor
          ?.KONTROLLERA_EJ_MOMS ?? false;

      if (konteringsforslagArray) {
        for (let index = 0; index < konteringsforslagArray.length; index++) {
          const konteringsforslag = konteringsforslagArray[index];

          const noMomsSelected = Boolean(konteringsforslag.INGEN_MOMS);

          const hasTouchedIngMoms =
            touched?._Associations?.Faktura?._Associations.Konteringsforslag?.[
              konteringsforslag.id
            ]?._Associations.IngMoms;

          const allKodstrangar = getKodstrangar(konteringsforslag);

          const hasMomspliktigKoddel = allKodstrangar.kontodelar.some(koddel =>
            konteringContext.konteringUtils.isNewSelectedKontodelMomspliktig(
              kodplanId,
              koddel.lopnr,
              koddel.id,
              bokfDatum
            )
          );

          const hasValidTolkadMomsats = fakturaattestContext.sysMoms?.some(
            sysMoms =>
              isNumberWithinMarginOfError(
                sysMoms.MOMS_SATS1,
                sysMomsTolerans?.OptionData ?? MOMSSATS_TOLERANS_DEFAULT_VALUE,
                kalkMomssats ?? 0
              )
          );

          //Validering på om användaren ändrar tolkad momssats
          const errorChangePreselectedMomssats =
            validateSvenskChangePreselectedMomssats(
              konteringsforslag.ING_MOMS_SATS,
              kalkMomssats,
              hasMomspliktigKoddel,
              hasValidTolkadMomsats,
              konteringsforslag.id
            );
          if (errorChangePreselectedMomssats) {
            errors.push(errorChangePreselectedMomssats);
          }

          if (
            momshantering ===
            TEDLRLeverantorfakturaMOMSHANTERINGEnum.SVENSK__ENLIGT_KODDEL
          ) {
            //Validering på om användaren tar bort tolkad momssats
            const errorDeletePreselectedMomssats =
              validateSvenskDeletePreselectedMomssats(
                konteringsforslag.ING_MOMS_SATS,
                hasMomspliktigKoddel,
                hasValidTolkadMomsats,
                konteringsforslag.id,
                skipMomsValidation || noMomsSelected
              );
            if (errorDeletePreselectedMomssats) {
              errors.push(errorDeletePreselectedMomssats);
            }
          }

          if (
            momshantering ===
            TEDLRLeverantorfakturaMOMSHANTERINGEnum.SVENSK__ENLIGT_KODDEL
          ) {
            //Validering om det inte finns en förvald momssats och användaren lämnar det det tomt
            const errorNoPreselectedMomssats =
              validateSvenskNoPreselectedMomssats(
                konteringsforslag.ING_MOMS_SATS,
                hasMomspliktigKoddel,
                hasTouchedIngMoms,
                attestTyp,
                hasValidTolkadMomsats,
                tolkatMomsbelopp,
                konteringsforslag.id
              );

            if (errorNoPreselectedMomssats) {
              errors.push(errorNoPreselectedMomssats);
            }
          }

          //Valdering på att ingeånde momsbelopp på konteringsförslagen inte överstiger det tolkade momsbeloppt.
          const ingaendeMomsbelopp = konteringsforslagArray.reduce(
            (ingaendeMoms, curr) => {
              if (curr.ING_MOMS_BELOPP) {
                return (ingaendeMoms += curr.ING_MOMS_BELOPP);
              }
              return ingaendeMoms;
            },
            0
          );

          const errorIngaendeMomsbeloppGreaterThanTolkatMomsbelopp =
            validateSvenskIngaendeMomsbeloppGreaterThanTolkatMomsbelopp(
              ingaendeMomsbelopp,
              hasMomspliktigKoddel,
              attestTyp,
              tolkatMomsbelopp,
              momsbeloppTolerans,
              konteringsforslag.id
            );

          if (errorIngaendeMomsbeloppGreaterThanTolkatMomsbelopp) {
            errors.push(errorIngaendeMomsbeloppGreaterThanTolkatMomsbelopp);
          }

          //Validering på att mottagingsattestanter inte få ange en momssats om fakturans tolkade momsbelopp är 0
          const noMomssatsWhenTolkatMomsbeloppIsZero =
            validateNoMomssatsWhenTolkatMomsbeloppIsZero(
              konteringsforslag.ING_MOMS_SATS,
              hasMomspliktigKoddel,
              tolkatMomsbelopp,
              attestTyp,
              konteringsforslag.id,
              skipMomsValidation || noMomsSelected
            );

          if (noMomssatsWhenTolkatMomsbeloppIsZero) {
            errors.push(noMomssatsWhenTolkatMomsbeloppIsZero);
          }
        }
      }

      return errors;
    };
  };
